import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import { OFFER_MEALS, offersApi, scheduleDays, titleCase, formatPrice } from '../../services/offersApi';
import { OfferShell, clearDraft, loadDraft } from './OfferShell';

type Status = 'verifying' | 'paid' | 'failed' | 'cancelled';

export default function OfferConfirmationPage() {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const reference = params.get('reference') || params.get('trxref') || '';
  const callbackStatus = (params.get('status') || '').toLowerCase();
  const draft = useMemo(() => loadDraft(slug), [slug]);
  const offerUrl = `/offers/${encodeURIComponent(slug)}`;

  const [status, setStatus] = useState<Status>(() =>
    ['cancelled', 'canceled', 'abandoned'].includes(callbackStatus) ? 'cancelled' : reference ? 'verifying' : 'failed'
  );
  const [receipt, setReceipt] = useState<{ campaign?: string; customer_name?: string; customer_email?: string; total_amount_paid?: number } | null>(null);
  const [message, setMessage] = useState<string>('');
  const [attempt, setAttempt] = useState(0);
  // Snapshot the order before the draft is cleared so the summary stays on screen.
  const [order] = useState(draft);

  useSEO({ title: status === 'paid' ? 'Reservation confirmed - BOQBOX' : 'Payment status - BOQBOX', description: 'Your special meal reservation.' });

  useEffect(() => {
    if (!reference || status === 'cancelled') return undefined;
    let cancelled = false;
    let tries = 0;
    setStatus('verifying');

    // Paystack can take a few seconds to settle; retry briefly before giving up.
    const check = async () => {
      tries += 1;
      try {
        const data = await offersApi.confirmPayment(reference);
        if (cancelled) return;
        setReceipt(data);
        setStatus('paid');
        clearDraft(slug);
      } catch (e) {
        if (cancelled) return;
        if (tries < 4) {
          window.setTimeout(check, 2500);
        } else {
          setMessage((e as Error).message);
          setStatus('failed');
        }
      }
    };
    check();
    return () => { cancelled = true; };
  }, [reference, slug, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = order ? scheduleDays(order.schedule) : [];
  const mealCount = order ? days.reduce((n, d) => n + OFFER_MEALS.reduce((m, meal) => m + (order.schedule[d]?.[meal] || []).reduce((q, s) => q + s.quantity, 0), 0), 0) : 0;

  return (
    <OfferShell backTo={offerUrl} backLabel="Back to offer">
      <div className="mo-container">
        <div className="mo-card mo-confirm" role="status" aria-live="polite">
          {status === 'verifying' ? (
            <>
              <div className="mo-confirm-icon" style={{ background: 'var(--orange)' }}><span className="mo-spinner" /></div>
              <h1>Confirming your payment…</h1>
              <p>This only takes a moment. Please don’t close this page.</p>
            </>
          ) : status === 'paid' ? (
            <>
              <div className="mo-confirm-icon" aria-hidden="true">✓</div>
              <h1>You’re all set{receipt?.customer_name ? `, ${receipt.customer_name.split(' ')[0]}` : ''}!</h1>
              <p>Your reservation{receipt?.campaign ? ` for ${receipt.campaign}` : ''} is confirmed. A receipt is on its way to {receipt?.customer_email || 'your email'}.</p>
              <div className="mo-receipt">
                {receipt?.total_amount_paid !== undefined ? <div className="mo-sum-row"><span>Amount paid</span><strong>{formatPrice(receipt.total_amount_paid)}</strong></div> : null}
                {order ? <div className="mo-sum-row"><span>Days</span><strong>{days.map((d) => titleCase(d.slice(0, 3))).join(', ')}</strong></div> : null}
                {order ? <div className="mo-sum-row"><span>Meals & items</span><strong>{mealCount}</strong></div> : null}
                {order ? <div className="mo-sum-row"><span>Fulfilment</span><strong>{order.fulfilment === 'pickup' ? 'Pickup (earlier days delivered)' : 'Delivery'}</strong></div> : null}
                {order?.customer.address && order.fulfilment === 'delivery' ? <div className="mo-sum-row"><span>Deliver to</span><strong style={{ textAlign: 'right' }}>{order.customer.address}</strong></div> : null}
                <div className="mo-sum-row"><span>Reference</span><strong style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{reference}</strong></div>
              </div>
              <div className="mo-actions">
                <Link className="mo-btn mo-btn-primary" to="/offers">Browse more offers</Link>
                <Link className="mo-btn mo-btn-ghost" to="/">BOQBOX home</Link>
              </div>
            </>
          ) : (
            <>
              <div className="mo-confirm-icon mo-fail" aria-hidden="true">!</div>
              <h1>{status === 'cancelled' ? 'Payment cancelled' : 'We couldn’t confirm your payment'}</h1>
              <p>
                {status === 'cancelled'
                  ? 'No money was taken. Your meal selection is saved, so you can try again.'
                  : message || 'If you were charged, don’t pay again. It may take a few minutes to confirm.'}
              </p>
              {reference ? <p style={{ fontSize: 13 }}>Reference: <strong>{reference}</strong></p> : null}
              <div className="mo-actions">
                {reference && status === 'failed' ? <button className="mo-btn mo-btn-primary" onClick={() => setAttempt((n) => n + 1)}>Check again</button> : null}
                {order ? <Link className="mo-btn mo-btn-ghost" to={`${offerUrl}/order?plan=${order.planId}`}>Back to my order</Link> : <Link className="mo-btn mo-btn-ghost" to={offerUrl}>Back to the offer</Link>}
              </div>
            </>
          )}
        </div>
      </div>
    </OfferShell>
  );
}
